import LocalesDrawer from '@components/Customization/LocalesDrawer';
import PoiTypeDrawer, { type PoiTypeDraft } from '@components/Customization/PoiTypes/PoiTypeDrawer';
import PoiTypeRow from '@components/Customization/PoiTypes/PoiTypeRow';
import Header from '@components/Shared/Layout/Header';
import SearchInput from '@components/Shared/SearchInput';
import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import { Box, Button, Divider, LinearProgress, Paper, Typography } from '@mui/material';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useAddPoiTypeMutation, useGetPoiTypesQuery, type PoiType } from '@store/api/poiTypeEndpoints';
import { useGetConfigQuery, useUpdateConfigMutation, type CustomizationLocales } from '@store/api/rootEndpoints';
import { useGetTeamDirectoryQuery } from '@store/api/teamEndpoints';
import { usePermission } from '@store/hooks';
import { type RootState } from '@store/store';
import { useMemo, useState } from 'react';
import { GlobeIcon, PlusIcon } from 'react-line-awesome';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

/** The catalogue: a list, a way to add to it, and the locale set the labels are written in. */
const CustomPoiTypesPage = () => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();
  const navigate = useNavigate();
  const { can } = usePermission();
  const sessionTeams = useSelector((state: RootState) => state.auth.user?.teams ?? []);

  const { data: poiTypes = [], isLoading } = useGetPoiTypesQuery();
  const { data: config } = useGetConfigQuery();

  const [addPoiType, { isLoading: isCreating }] = useAddPoiTypeMutation();
  const [updateConfig, { isLoading: isSavingLocales }] = useUpdateConfigMutation();

  const [search, setSearch] = useState('');
  const [newTypeOpen, setNewTypeOpen] = useState(false);
  const [localesOpen, setLocalesOpen] = useState(false);

  /**
   * Teams a type may be created in.
   *
   * A global author — sysadmin, admin — may create one in any team, and typically belongs
   * to none, so their own memberships cannot produce the list: filtering those left the
   * list empty and hid the button from the people most able to use it. They get the whole
   * directory; everyone else gets the teams their own role grants it in.
   */
  const canCreateAnywhere = can('poi_type:post');
  const { data: teamDirectory = [] } = useGetTeamDirectoryQuery(undefined, { skip: !canCreateAnywhere });

  const ownableTeams = useMemo(
    () => (canCreateAnywhere
      ? teamDirectory.map((team) => ({ id: team._id, name: team.name }))
      : sessionTeams
        .filter((team) => can('poi_type:post', team.id))
        .map((team) => ({ id: team.id, name: team.name }))),
    [canCreateAnywhere, teamDirectory, sessionTeams, can]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return poiTypes;

    return poiTypes.filter((poiType) =>
      Object.values(poiType.name).some((value) => value?.toLowerCase().includes(query))
      || poiType.description?.toLowerCase().includes(query));
  }, [poiTypes, search]);

  const openType = (poiType: PoiType) =>
    navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPE, { poiTypeId: poiType._id }));

  const handleCreate = async (draft: PoiTypeDraft) => {
    const result = await addPoiType({
      teamId: draft.teamId,
      name: draft.name,
      description: draft.description,
      inheritFrom: draft.inheritFrom,
      fields: [],
      groups: [],
    });

    if (result.error) return;

    setNewTypeOpen(false);

    // Straight to the new type's page: it has no fields yet, and that is the next thing
    // the author is here to do.
    if ('data' in result && result.data) {
      navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPE, { poiTypeId: result.data.id }));
    }
  };

  // Server config is the only place the locale set lives, so changing it means sending the
  // whole config back — `auth` included, or the API would reject the body.
  const handleSaveLocales = async (customizationLocales: CustomizationLocales) => {
    if (!config) return;

    const result = await updateConfig({ auth: config.auth, customizationLocales });

    if (!result.error) setLocalesOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            marginBottom: '12px'
          }}
        >
          <SearchInput
            text={search}
            placeholder={t('Customization_SearchPoiTypes')}
            action={setSearch}
            count={filtered.length}
          />
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            {can('server_config:put') && (
              <Button variant="outlined" startIcon={<GlobeIcon />} onClick={() => setLocalesOpen(true)}>
                {t('Customization_EditLocales')}
              </Button>
            )}
            {ownableTeams.length > 0 && (
              <Button variant="contained" startIcon={<PlusIcon />} onClick={() => setNewTypeOpen(true)}>
                {t('Customization_NewPoiType')}
              </Button>
            )}
          </Box>
        </Box>

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {isLoading && <LinearProgress />}

          {!isLoading && filtered.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              {t('Customization_NoPoiTypes')}
            </Typography>
          )}

          {filtered.map((poiType, position) => (
            <Box key={poiType._id}>
              {position > 0 && <Divider />}
              <PoiTypeRow poiType={poiType} onOpen={() => openType(poiType)} />
            </Box>
          ))}
        </Paper>
      </Box>

      <PoiTypeDrawer
        open={newTypeOpen}
        poiType={null}
        parentCandidates={poiTypes}
        teams={ownableTeams}
        saving={isCreating}
        onClose={() => setNewTypeOpen(false)}
        onSave={handleCreate}
      />

      <LocalesDrawer
        open={localesOpen}
        locales={locales}
        saving={isSavingLocales}
        onClose={() => setLocalesOpen(false)}
        onSave={handleSaveLocales}
      />
    </Box>
  );
};

export default CustomPoiTypesPage;
